import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError, UploadProgress } from '../api/client';
import { subtitlesApi, type SubtitleBatchesParams } from '../api/endpoints';
import type {
  Id,
  Page,
  SubtitleBatch,
  SubtitleBatchRequest,
  SubtitleLayoutRequest,
  SubtitlePreset,
  SubtitleSettings,
} from '../types/api';
import { queryKeys } from './keys';

const RUNNING_POLL_MS = 2500;
// Idle batches are still refetched: their presigned frame and download links expire.
const IDLE_POLL_MS = 15_000;

export const SUBTITLE_MAX_FILES = 20;
export const SUBTITLE_VIDEO_EXTS = ['.mp4', '.mov', '.m4v'] as const;

export function isSubtitleBatchRunning(batch: SubtitleBatch | undefined): boolean {
  return (
    batch?.status === 'pending' || batch?.status === 'transcribing' || batch?.status === 'rendering'
  );
}

export function useSubtitlePresets(): UseQueryResult<SubtitlePreset[], ApiError> {
  return useQuery<SubtitlePreset[], ApiError>({
    queryKey: queryKeys.subtitlePresets,
    queryFn: () => subtitlesApi.presets(),
    staleTime: Infinity,
  });
}

export function useSubtitleSettings(): UseQueryResult<SubtitleSettings, ApiError> {
  return useQuery<SubtitleSettings, ApiError>({
    queryKey: queryKeys.subtitleSettings,
    queryFn: () => subtitlesApi.settings(),
    staleTime: Infinity,
  });
}

export function useSubtitleBatches(
  params: SubtitleBatchesParams = {},
): UseQueryResult<Page<SubtitleBatch>, ApiError> {
  return useQuery<Page<SubtitleBatch>, ApiError>({
    queryKey: queryKeys.subtitleBatches(params),
    queryFn: () => subtitlesApi.list(params),
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some(isSubtitleBatchRunning) ? RUNNING_POLL_MS : IDLE_POLL_MS,
  });
}

function useRefreshBatches() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: queryKeys.subtitlesRoot });
}

export function useStartSubtitles(onProgress?: (progress: UploadProgress) => void) {
  const refresh = useRefreshBatches();
  return useMutation<SubtitleBatch, ApiError, SubtitleBatchRequest>({
    mutationFn: (payload) => subtitlesApi.create(payload, onProgress),
    retry: false,
    onSuccess: refresh,
  });
}

export function useRestyleSubtitles() {
  const refresh = useRefreshBatches();
  return useMutation<SubtitleBatch, ApiError, { batchId: Id; preset: string }>({
    mutationFn: ({ batchId, preset }) => subtitlesApi.restyle(batchId, preset),
    onSuccess: refresh,
  });
}

export function useSubtitleLayout() {
  const refresh = useRefreshBatches();
  return useMutation<
    SubtitleBatch,
    ApiError,
    { batchId: Id; index: number | null; layout: SubtitleLayoutRequest }
  >({
    mutationFn: ({ batchId, index, layout }) =>
      index === null
        ? subtitlesApi.setBatchLayout(batchId, layout)
        : subtitlesApi.setItemLayout(batchId, index, layout),
    onSuccess: refresh,
  });
}
